import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import TiltCard from './TiltCard';

const BlogSectionWrapper = styled.section`
  padding: ${props => props.theme.spacing.xxxl} ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.backgroundSecondary};
  position: relative;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: ${props => props.theme.typography.weights.extrabold};
  margin-bottom: ${props => props.theme.spacing.xl};
  background: ${props => props.theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const BlogCard = styled(motion.div)`
  background: ${props => props.theme.colors.surfaceCard};
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;

  &:hover {
    box-shadow: ${props => props.theme.shadows.cardHover};
    border-color: ${props => props.theme.colors.accentPurple};
    
    img {
      transform: scale(1.05);
    }
  }
`;

const CardLink = styled(Link)`
  display: block;
  color: inherit;
`;

const ThumbnailContainer = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  position: relative;
  background: ${props => props.theme.gradients.subtle};
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const PlaceholderThumbnail = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.gradients.subtle};
  color: ${props => props.theme.colors.accentPurple};
  font-weight: bold;
`;

const BlogContent = styled.div`
  padding: ${props => props.theme.spacing.xl};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const BlogMeta = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: 0.85rem;
  color: ${props => props.theme.colors.subtextGray};
  font-weight: ${props => props.theme.typography.weights.medium};
`;

const BlogTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.textLight};
  margin-bottom: ${props => props.theme.spacing.sm};
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MarkdownPreview = styled.div`
  color: ${props => props.theme.colors.subtextGray};
  font-size: 0.95rem;
  line-height: 1.6;
  flex: 1;
  
  max-height: 100px;
  overflow: hidden;
  position: relative;
  mask-image: linear-gradient(180deg, black 60%, transparent 100%);
  -webkit-mask-image: linear-gradient(180deg, black 60%, transparent 100%);

  img { display: none; }
  h1, h2, h3, h4 { font-size: 1rem; font-weight: bold; margin: 0; } 
  
  p { margin-bottom: 0.5rem; }
  strong { font-weight: 600; color: ${props => props.theme.colors.textDark}; }
  em { font-style: italic; }
`;

const ReadMore = styled.div`
  margin-top: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.accentPurple};
  font-weight: ${props => props.theme.typography.weights.semibold};
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BlogSection = ({ refreshTrigger }) => {
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    const fetchBlogs = async () => {
      if (!supabase) return;

      try {
        // First try to get featured blogs
        let { data, error } = await supabase
          .from('blogs')
          .select('*')
          .eq('status', 'published')
          .eq('featured', true)
          .order('created_at', { ascending: false })
          .limit(3);

        if (error) throw error;

        // If no featured blogs, fall back to latest 3
        if (!data || data.length === 0) {
          const fallback = await supabase
            .from('blogs')
            .select('*')
            .eq('status', 'published')
            .order('created_at', { ascending: false })
            .limit(3);

          if (fallback.error) throw fallback.error;
          data = fallback.data;
        }

        setBlogs(data || []);
      } catch (error) {
        console.error('Error loading blogs:', error);
      }
    };

    fetchBlogs();
  }, [refreshTrigger]);

  if (blogs.length === 0) return null;

  return (
    <BlogSectionWrapper id="blog">
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Latest Updates
        </SectionTitle>

        <BlogGrid>
          <AnimatePresence>
            {blogs.map((blog, index) => (
              <TiltCard key={blog.id} maxTilt={3} glareEffect={false}>
                <CardLink to={`/blog/${blog.id}`} aria-label={`Read ${blog.title}`}>
                  <BlogCard
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <ThumbnailContainer>
                      {blog.thumbnail_url ? (
                        <Thumbnail src={blog.thumbnail_url} alt={blog.title} />
                      ) : (
                        <PlaceholderThumbnail>No Image</PlaceholderThumbnail>
                      )}
                    </ThumbnailContainer>

                    <BlogContent>
                      <BlogMeta>
                        <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                        <span>3 min read</span>
                      </BlogMeta>
                      <BlogTitle>{blog.title}</BlogTitle>
                      <MarkdownPreview>
                        <ReactMarkdown allowedElements={['p', 'strong', 'em']}>
                          {blog.content}
                        </ReactMarkdown>
                      </MarkdownPreview>
                      <ReadMore>Read Full Article &rarr;</ReadMore>
                    </BlogContent>
                  </BlogCard>
                </CardLink>
              </TiltCard>
            ))}
          </AnimatePresence>
        </BlogGrid>
      </Container>
    </BlogSectionWrapper>
  );
};

export default BlogSection;
